import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  setCancelationErrorMessage,
  setCancelationLoading
} from 'store/reducers/bookingAppointmentSlice';

import { normalizeHairSystemsResponse } from 'utils/helpers/hairSystems';
import {
  buildSecureMessageReplyItem,
  getSecureMessageArchiveFlag,
  getSecureMessageRowIds,
  getSecureMessageThreadId,
  isSecureMessageReplySuccessful,
  mergeSecureMessagesLists,
  resolveSecureMessageErrorMessage,
  resolveSecureMessageReplyMsgId,
  sortSecureMessageConversation
} from 'utils/helpers/secureMessages';
import { normalizeSystemModules } from 'utils/helpers/systemModules';

import {
  AppointmentsType,
  ArchiveSecureMessageResponse,
  CheckoutParameters,
  CheckoutResponse,
  ExecuteBalancePaymentParams,
  GetAllSecureMessagesParams,
  GetHairSystemsArgs,
  GetSecureMessagesParams,
  GetSecureMessagesQueryResult,
  GetSecureMessagesResponse,
  HairSortOrder,
  HairSystemsPage,
  MarkSecureMessageUnreadParams,
  MarkSecureMessageUnreadResponse,
  MembershipDetailsInterface,
  MembershipInterface,
  ReplySecureMessageParams,
  ReplySecureMessageResponse,
  SecureMessageArchiveFlag,
  SecureMessageConversationItem,
  SecureMessageListItem,
  SecureMessageListStatus,
  SecureMessageListType,
  SetSecureMessageArchivedParams,
  ShopMembership,
  ShopMembershipDetails,
  SystemModulesResponse,
  SystemPreferencesResponse,
  UpcomingAppointment
} from 'types';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import api from '../api/client.api';
import type { RootState } from '../store';
import { serializeQueryError } from '../utils/rtkQueryError';

const mapSecureMessagesList = (
  response?: GetSecureMessagesResponse | null
): SecureMessageListItem[] =>
  (response?.list ?? []).map((item) => {
    const threadId = getSecureMessageThreadId(item);

    return {
      ...item,
      msg_id: threadId,
      thread_id: threadId,
      msgIds: getSecureMessageRowIds(item),
      subject: item.subject || item.msg_subject || ''
    };
  });

/** `has_more_data` arrives as a JSON boolean; older builds send 0/1. */
const hasMoreSecureMessages = (
  response?: GetSecureMessagesResponse | null
): boolean => response?.has_more_data === true || response?.has_more_data === 1;

const EMPTY_SECURE_MESSAGES_PAGE = { list: [], hasMoreData: false };

const fetchSecureMessagesByType = async (
  list_type: SecureMessageListType,
  limit: number,
  skip: number,
  is_archive: SecureMessageArchiveFlag
) => {
  const { data } = await api.getSecMessages({
    list_type,
    limit,
    skip,
    is_archive
  });
  const response = data as GetSecureMessagesResponse;

  return {
    list: mapSecureMessagesList(response),
    hasMoreData: hasMoreSecureMessages(response)
  };
};

const ACTIVE_LIST = 'ACTIVE_LIST';
const DETAILS = 'DETAILS';
const APPOINTMENTS = 'APPOINTMENTS';
const SECURE_MESSAGES = 'SECURE_MESSAGES';
const SECURE_MESSAGE_CONVERSATION = 'SECURE_MESSAGE_CONVERSATION';

export const userProfileApi = createApi({
  reducerPath: 'userProfileApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  tagTypes: [
    ACTIVE_LIST,
    DETAILS,
    APPOINTMENTS,
    SECURE_MESSAGES,
    SECURE_MESSAGE_CONVERSATION
  ],
  endpoints: (builder) => ({
    getMembershipList: builder.query<ShopMembership[], number>({
      queryFn: async (locid: number) => {
        try {
          const { data } = await api.getMembershipList(locid);

          return { data };
        } catch (error) {
          console.error(`Error while get membership list: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      }
    }),
    getMembershipDetails: builder.query<
      ShopMembershipDetails,
      {
        id: number;
        locid: number;
      }
    >({
      queryFn: async (params: { id: number; locid: number }) => {
        try {
          const { data } = await api.getMembershipDetails(params);

          return { data };
        } catch (error) {
          console.error(`Error while get membership details: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      }
    }),
    getActiveMemberships: builder.query<MembershipInterface[], string>({
      queryFn: async () => {
        try {
          const { data } = await api.getActiveMemberships();

          return { data };
        } catch (error) {
          console.error(`Error while get memberships list: ${error.message}`);
          return { error: serializeQueryError(error) };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ACTIVE_LIST' as const, id })),
              ACTIVE_LIST
            ]
          : [ACTIVE_LIST]
    }),
    getActiveMembershipDetails: builder.query<
      MembershipDetailsInterface,
      string
    >({
      queryFn: async (id: string) => {
        try {
          const { data } = await api.getActiveMembershipDetails(id);

          return { data };
        } catch (error) {
          console.error(`Error while get membership details: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      },
      providesTags: [DETAILS]
    }),
    updateMembershipStatus: builder.mutation<
      MembershipDetailsInterface & { success: boolean },
      string
    >({
      queryFn: async (id: string) => {
        try {
          const { data } = await api.updateMembershipStatus(id);

          return { data };
        } catch (error) {
          console.error(`Error while get membership details: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      },

      invalidatesTags: [DETAILS]
    }),
    updateMembershipPayment: builder.mutation<
      MembershipDetailsInterface & { success: boolean },
      { id: string; pymtId: number; onSuccess: () => void }
    >({
      queryFn: async ({
        id,
        pymtId,
        onSuccess
      }: {
        id: string;
        pymtId: number;
        onSuccess: () => void;
      }) => {
        try {
          const { data } = await api.updateMembershipPayment(id, pymtId);
          if (data) {
            onSuccess();
          }

          return { data };
        } catch (error) {
          console.error(
            `Error while updating membership payment methods: ${
              error?.response?.data?.error ?? ''
            }`
          );

          return { error: error?.response?.data?.error };
        }
      },

      invalidatesTags: [DETAILS]
    }),
    executeMembershipPayment: builder.mutation<
      MembershipDetailsInterface & { success: boolean; error_text: string },
      number
    >({
      queryFn: async (id: number) => {
        try {
          const { data } = await api.membershipsExecPayment(id);

          return { data };
        } catch (error) {
          console.error(
            `Error while paying membership billing: ${error.message}`
          );

          return { error: serializeQueryError(error) };
        }
      },
      invalidatesTags: (res) => (res?.success ? [DETAILS] : [])
    }),
    executeBalancePayment: builder.mutation<
      MembershipDetailsInterface & { success: boolean; error: string },
      ExecuteBalancePaymentParams
    >({
      queryFn: async (params: ExecuteBalancePaymentParams) => {
        try {
          const { data } = await api.executeBalancePayment(params);

          return { data };
        } catch (error) {
          console.error(
            `Error while execute Balance Payment: ${error.message}`
          );

          return { error: error.response.data.error ?? error.message };
        }
      }
    }),
    getClAppointments: builder.query<
      UpcomingAppointment[],
      {
        itemType: AppointmentsType;
        number?: number;
      }
    >({
      queryFn: async ({ itemType, number = 10 }) => {
        try {
          const { data } = await api.getClAppointments(itemType, number);

          return { data };
        } catch (error) {
          console.error(`Error fetching appointments: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      }
    }),
    cancelAppointment: builder.mutation<
      { success: boolean; message?: string },
      {
        id: number;
        onSuccess?: () => void;
      }
    >({
      queryFn: async ({ id, onSuccess = () => {} }, { dispatch }) => {
        dispatch(setCancelationLoading(true));
        try {
          const { data } = await api.cancelAppointment(id);
          onSuccess();
          return { data };
        } catch (error) {
          console.error(`Error while cancel appointment: ${error.message}`);
          dispatch(
            setCancelationErrorMessage(
              error.response?.data?.error || error.message
            )
          );

          return { error: error.response?.data?.error || error.message };
        } finally {
          dispatch(setCancelationLoading(false));
        }
      },
      async onQueryStarted(props, { dispatch, queryFulfilled }) {
        const { data: response } = await queryFulfilled;

        if (response.success) {
          dispatch(
            userProfileApi.util.updateQueryData(
              'getClAppointments',
              { itemType: AppointmentsType.future },
              (draft: UpcomingAppointment[]) => {
                if (draft) {
                  return draft.filter(
                    (appointment: UpcomingAppointment) =>
                      appointment.confirmation_num !== props.id
                  );
                }
                return draft;
              }
            )
          );
        }
      }
    }),
    purchasesMembership: builder.mutation<CheckoutResponse, CheckoutParameters>(
      {
        queryFn: async (params: CheckoutParameters) => {
          try {
            const { data } = await api.cartCompleteCheckout(params);

            return { data };
          } catch (error) {
            console.error(`Error while purchase membership: ${error.message}`);

            return { error: error.message };
          }
        },
        invalidatesTags: (result) => (result?.success ? [ACTIVE_LIST] : [])
      }
    ),
    getSystemPreferences: builder.query<SystemPreferencesResponse, string>({
      queryFn: async () => {
        try {
          const { data } = await api.checkSyncStatus();

          return { data };
        } catch (error) {
          console.error(`Error while get system preferences: ${error.message}`);

          return { error: error.message };
        }
      }
    }),
    getSystemModules: builder.query<SystemModulesResponse, void>({
      queryFn: async () => {
        try {
          const { data } = await api.getSysModules();
          const modules = normalizeSystemModules(data);

          // An unrecognised payload is as unusable as a failed request (a tenant that
          // hasn't shipped `sys-modules` may answer 200 with an error envelope). Report it
          // as an error so callers latch onto the static-config fallback instead of reading
          // it as "every section flag absent".
          if (!modules) {
            return { error: 'sys-modules returned an unrecognised payload' };
          }

          return { data: modules };
        } catch (error) {
          console.error(`Error while get system modules: ${error.message}`);

          // `error.message` can be undefined, and RTK Query reads a falsy `error` as a
          // successful `undefined` payload — always send a non-empty string.
          return { error: error.message || 'sys-modules request failed' };
        }
      }
    }),
    getHairSystems: builder.query<HairSystemsPage, GetHairSystemsArgs>({
      queryFn: async ({ status, limit, skip = 0 }) => {
        try {
          // `get-cl-hair` returns a bare array with no paging metadata, so ask for one
          // row more than the page to learn whether another page exists.
          const { data } = await api.getClHair({
            status_id: status,
            sort_by: HairSortOrder.NewestFirst,
            first: limit + 1,
            skip
          });
          const list = normalizeHairSystemsResponse(data);

          if (!list) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'get-cl-hair returned an unrecognised payload'
              }
            };
          }

          return {
            data: {
              list: list.slice(0, limit),
              hasMore: list.length > limit
            }
          };
        } catch (error) {
          console.error(`Error while get hair systems: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      }
    }),
    getSecureMessages: builder.query<
      GetSecureMessagesQueryResult,
      GetSecureMessagesParams
    >({
      queryFn: async (params: GetSecureMessagesParams) => {
        try {
          const { data } = await api.getSecMessages(params);
          const response = data as GetSecureMessagesResponse;

          return {
            data: {
              list: mapSecureMessagesList(response),
              hasMoreData: hasMoreSecureMessages(response)
            }
          };
        } catch (error) {
          console.error(`Error while get secure messages: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      },
      providesTags: [SECURE_MESSAGES]
    }),
    getAllSecureMessages: builder.query<
      GetSecureMessagesQueryResult,
      GetAllSecureMessagesParams
    >({
      queryFn: async (params: GetAllSecureMessagesParams) => {
        try {
          const {
            limit = 25,
            sentSkip = 0,
            receivedSkip = 0,
            status = SecureMessageListStatus.Active,
            includeSent = true,
            includeReceived = true
          } = params;
          const isArchive = getSecureMessageArchiveFlag(status);

          const [sent, received] = await Promise.all([
            includeSent
              ? fetchSecureMessagesByType(
                  SecureMessageListType.Sent,
                  limit,
                  sentSkip,
                  isArchive
                )
              : Promise.resolve(EMPTY_SECURE_MESSAGES_PAGE),
            includeReceived
              ? fetchSecureMessagesByType(
                  SecureMessageListType.Received,
                  limit,
                  receivedSkip,
                  isArchive
                )
              : Promise.resolve(EMPTY_SECURE_MESSAGES_PAGE)
          ]);

          return {
            data: {
              list: mergeSecureMessagesLists([sent.list, received.list]),
              hasMoreData: sent.hasMoreData || received.hasMoreData,
              sentHasMore: sent.hasMoreData,
              receivedHasMore: received.hasMoreData
            }
          };
        } catch (error) {
          console.error(
            `Error while get all secure messages: ${error.message}`
          );

          return { error: serializeQueryError(error) };
        }
      },
      providesTags: [SECURE_MESSAGES]
    }),
    getSecureMessageConversation: builder.query<
      SecureMessageConversationItem[],
      number
    >({
      queryFn: async (msg_id: number) => {
        try {
          const { data } = await api.getSecMessageConversation(msg_id);
          const conversation = Array.isArray(data)
            ? data
            : (data as { list?: SecureMessageConversationItem[] })?.list ?? [];

          return { data: sortSecureMessageConversation(conversation) };
        } catch (error) {
          console.error(
            `Error while get secure message conversation: ${error.message}`
          );

          return { error: serializeQueryError(error) };
        }
      },
      providesTags: (_result, _error, msg_id) => [
        { type: SECURE_MESSAGE_CONVERSATION, id: msg_id }
      ],
      async onQueryStarted(_msg_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(userProfileApi.util.invalidateTags([SECURE_MESSAGES]));
        } catch {
          // Conversation failed — keep list cache unchanged
        }
      }
    }),
    replySecureMessage: builder.mutation<
      ReplySecureMessageResponse,
      ReplySecureMessageParams
    >({
      queryFn: async (params: ReplySecureMessageParams) => {
        try {
          const { data } = await api.replySecMessage(params);

          if (!isSecureMessageReplySuccessful(data)) {
            return {
              error: serializeQueryError(
                new Error(
                  resolveSecureMessageErrorMessage(
                    data,
                    'Failed to send message'
                  )
                )
              )
            };
          }

          return { data };
        } catch (error: unknown) {
          console.error(`Error while reply secure message: ${error}`);

          return { error: serializeQueryError(error) };
        }
      },
      async onQueryStarted(
        { msg_id, msg_text },
        { dispatch, queryFulfilled, getState }
      ) {
        try {
          const { data: response } = await queryFulfilled;

          if (!isSecureMessageReplySuccessful(response)) {
            return;
          }

          const state = (getState as () => RootState)();
          const existing =
            userProfileApi.endpoints.getSecureMessageConversation.select(
              msg_id
            )(state).data ?? [];
          const { user } = state.user;
          const template =
            existing.find((message) => message.msg_direction === 'out') ??
            existing[0];
          const optimisticMsgId = resolveSecureMessageReplyMsgId(
            response.msg_id,
            existing
          );
          const newMessage = buildSecureMessageReplyItem(
            optimisticMsgId,
            msg_text,
            user,
            template
          );

          if (
            !existing.some((message) => message.msg_id === newMessage.msg_id)
          ) {
            dispatch(
              userProfileApi.util.upsertQueryData(
                'getSecureMessageConversation',
                msg_id,
                sortSecureMessageConversation([...existing, newMessage])
              )
            );
          }
        } catch {
          // Mutation failed — skip cache update
        }
      },
      invalidatesTags: (result, error) =>
        isSecureMessageReplySuccessful(result) && !error
          ? [SECURE_MESSAGES]
          : []
    }),
    markSecureMessageUnread: builder.mutation<
      MarkSecureMessageUnreadResponse,
      MarkSecureMessageUnreadParams
    >({
      queryFn: async (params: MarkSecureMessageUnreadParams) => {
        try {
          const { data } = await api.unreadSecMessage(params);

          if (!isSecureMessageReplySuccessful(data)) {
            return {
              error: serializeQueryError(
                new Error(
                  resolveSecureMessageErrorMessage(
                    data,
                    'Failed to mark message as unread'
                  )
                )
              )
            };
          }

          return { data };
        } catch (error: unknown) {
          console.error(`Error while mark secure message unread: ${error}`);

          return { error: serializeQueryError(error) };
        }
      },
      invalidatesTags: (result, error) =>
        isSecureMessageReplySuccessful(result) && !error
          ? [SECURE_MESSAGES]
          : []
    }),
    /**
     * archive-sec-message flags one row at a time, so a thread that has both a sent
     * and a received row needs every row toggled — otherwise it would show up as
     * active and archived at the same time.
     */
    setSecureMessageArchived: builder.mutation<
      ArchiveSecureMessageResponse,
      SetSecureMessageArchivedParams
    >({
      queryFn: async ({
        msgIds,
        isArchived
      }: SetSecureMessageArchivedParams) => {
        const action = isArchived ? 'archive' : 'unarchive';

        if (!msgIds.length) {
          return {
            error: serializeQueryError(
              new Error(`No message selected to ${action}`)
            )
          };
        }

        try {
          const responses = await Promise.all(
            msgIds.map((msg_id) =>
              api.archiveSecMessage(
                isArchived ? { msg_id } : { msg_id, revert: 1 }
              )
            )
          );
          const failed = responses.find(
            ({ data }) => !isSecureMessageReplySuccessful(data)
          );

          if (failed) {
            return {
              error: serializeQueryError(
                new Error(
                  resolveSecureMessageErrorMessage(
                    failed.data,
                    `Failed to ${action} message`
                  )
                )
              )
            };
          }

          return { data: responses[responses.length - 1].data };
        } catch (error: unknown) {
          console.error(
            `Error while trying to ${action} secure message: ${error}`
          );

          return { error: serializeQueryError(error) };
        }
      },
      invalidatesTags: (result, error) =>
        isSecureMessageReplySuccessful(result) && !error
          ? [SECURE_MESSAGES]
          : []
    })
  })
});

export const {
  useGetMembershipListQuery,
  useGetActiveMembershipsQuery,
  useGetMembershipDetailsQuery,
  useGetActiveMembershipDetailsQuery,
  useUpdateMembershipStatusMutation,
  useUpdateMembershipPaymentMutation,
  useExecuteMembershipPaymentMutation,
  useExecuteBalancePaymentMutation,
  usePurchasesMembershipMutation,
  useGetClAppointmentsQuery,
  useCancelAppointmentMutation,
  useGetSystemPreferencesQuery,
  useGetSystemModulesQuery,
  useGetHairSystemsQuery,
  useGetSecureMessagesQuery,
  useGetAllSecureMessagesQuery,
  useGetSecureMessageConversationQuery,
  useReplySecureMessageMutation,
  useMarkSecureMessageUnreadMutation,
  useSetSecureMessageArchivedMutation
} = userProfileApi;
